/**
 * Error utility functions for the delivery system
 */

/**
 * The delivery service returns a 400 with a structured body on validation
 * failures, e.g:
 *
 * {
 *   "success": false,
 *   "message": "Update failed: request has invalid status for updates",
 *   "hasError": true,
 *   "errors": ["Cannot update a request with status Completed"]
 * }
 *
 * A few code paths in the service wrap raw exception text into `errors`, which
 * can contain internal infrastructure detail (table names, stack context, etc).
 * Those entries are never shown to the user; only the curated validation
 * messages and the top level `message` are safe to display.
 */
const RAW_EXCEPTION_PATTERNS = [
    /^Exception occurred:/i,
    /^Error updating item\b.*:/i,
    /^Error creating new item:/i,
    /^Failed to add new items:/i,
    /^Error removing item\b.*:/i,
    /^Request updated but event publishing failed:/i
];

const isDisplayableError = (entry) => typeof entry === "string" && entry.trim() && !RAW_EXCEPTION_PATTERNS.some((pattern) => pattern.test(entry.trim()));

const asText = (value) => (typeof value === "string" && value.trim() ? value.trim() : null);

/**
 * Extract a user facing message from a delivery service error.
 *
 * @param {Object} error - The error thrown by an RTK Query `unwrap()`, or a plain Error.
 * @param {string} fallback - Message to use when nothing usable can be extracted.
 * @returns {string} - A message safe to show in a notification banner.
 */
export const getDeliveryErrorMessage = (error, fallback = "Unknown error") => {
    try {
        if (!error) return fallback;

        // RTK Query rejections look like { status, data }, where data is the response body
        const data = error.data ?? error;

        const bodyText = asText(data);
        if (bodyText) return bodyText;

        const details = Array.isArray(data?.errors) ? data.errors.filter(isDisplayableError).map((entry) => entry.trim()) : [];

        if (details.length) {
            const shown = details.slice(0, 3).join("; ");
            return details.length > 3 ? `${shown} (+${details.length - 3} more)` : shown;
        }

        return asText(data?.message) || asText(data?.error) || asText(error.message) || fallback;
    } catch (extractionError) {
        console.error("Failed to extract delivery error message: ", extractionError);
        return fallback;
    }
};
